import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ChevronLeft, Download, Loader2, Printer } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { formatErr } from '../../../components/admin/billing/shared';

const todayIST = () => new Date(Date.now() + 5.5 * 3600 * 1000).toISOString().slice(0, 10);

const save = (url, name) => {
  const a = document.createElement('a');
  a.href = url; a.download = name; a.click();
};

export default function TodayCards() {
  const { authHeader } = useAuth();
  const [cards, setCards] = useState(null);
  const [err, setErr] = useState('');
  const day = todayIST();

  useEffect(() => {
    let urls = [];
    (async () => {
      try {
        const { data } = await axios.get(`${API}/appointments`, {
          headers: authHeader(), params: { status: 'active', date_from: day, date_to: day, limit: 200 } });
        const rows = (data.rows || []).sort((a, b) => (a.slot_window || '').localeCompare(b.slot_window || ''));
        const out = await Promise.all(rows.map(async (r) => {
          const { data: blob } = await axios.get(`${API}/appointments/${r.id}/card`, { headers: authHeader(), responseType: 'blob' });
          const url = URL.createObjectURL(blob);
          urls.push(url);
          return { id: r.id, ref: r.ref_no || r.id, url, window: r.slot_window, name: [r.customer?.prefix, r.customer?.name].filter(Boolean).join(' ') };
        }));
        setCards(out);
      } catch (e) { setErr(formatErr(e)); setCards([]); }
    })();
    return () => urls.forEach((u) => URL.revokeObjectURL(u));
  }, []); // eslint-disable-line

  const fileName = (c) => `Appointment_${String(c.ref).replace(/\//g, '_')}.png`;
  return (
    <div className="space-y-4" data-testid="today-cards-page">
      <style>{'@media print { aside, header, nav, .no-print, [data-testid="help-fab"] { display: none !important; } main { padding: 0 !important; } .cards-grid { display: block !important; } .card-sheet { break-after: page; page-break-after: always; box-shadow: none !important; border: 0 !important; } .card-sheet img { max-height: 98vh; width: auto; margin: 0 auto; } }'}</style>
      <div className="no-print flex flex-wrap items-center gap-2">
        <Link to="/admin/appointments" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-teal-700" data-testid="today-cards-back"><ChevronLeft size={16} /> Appointments</Link>
        <h1 className="text-xl font-semibold text-slate-900 ml-2">Today's cards</h1>
        <span className="text-sm text-slate-500" data-testid="today-cards-count">{cards ? `${cards.length} appointment${cards.length === 1 ? '' : 's'}` : ''}</span>
        <div className="ml-auto flex gap-2">
          <button type="button" disabled={!cards?.length} onClick={() => cards.forEach((c, i) => setTimeout(() => save(c.url, fileName(c)), i * 300))} className="inline-flex items-center gap-1.5 border border-slate-300 hover:bg-slate-50 disabled:opacity-50 text-sm font-semibold px-3 py-2 rounded-lg" data-testid="today-cards-download-all"><Download size={14} /> Download all</button>
          <button type="button" disabled={!cards?.length} onClick={() => window.print()} className="inline-flex items-center gap-1.5 bg-teal-600 hover:bg-teal-700 disabled:bg-teal-300 text-white text-sm font-semibold px-3 py-2 rounded-lg" data-testid="today-cards-print"><Printer size={14} /> Print all</button>
        </div>
      </div>
      {err && <div className="no-print text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2" data-testid="today-cards-error">{err}</div>}
      {!cards && <div className="py-16 grid place-items-center"><Loader2 className="animate-spin text-slate-400" /></div>}
      {cards && !cards.length && !err && <p className="text-sm text-slate-500 py-10 text-center" data-testid="today-cards-empty">No pending or confirmed appointments today.</p>}
      <div className="cards-grid grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {cards?.map((c) => (
          <div key={c.id} className="card-sheet bg-white border border-slate-200 rounded-xl p-2 shadow-sm" data-testid={`today-card-${c.id}`}>
            <img src={c.url} alt={`Card ${c.ref}`} className="w-full rounded-lg" />
            <div className="no-print flex items-center justify-between px-1 pt-2 text-xs text-slate-500">
              <span>{c.window} · {c.name}</span>
              <button type="button" onClick={() => save(c.url, fileName(c))} className="inline-flex items-center gap-1 font-semibold text-teal-700 hover:text-teal-900" data-testid={`today-card-download-${c.id}`}><Download size={12} /> Download</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
